import { inject, Injectable, OnDestroy } from "@angular/core";
import {
    asapScheduler,
    concat,
    firstValueFrom,
    Observable,
    ReplaySubject,
    Subject
} from "rxjs";
import { last, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import {
    ConfirmDialogResponse,
    SkogsvardAtgardsGrupp,
    SkogsvardAtgardsMetod,
    SkogsvardAtgardsTyp,
    SkogsvardForyngringssatt,
    VirkeskopAffarsformer,
    VirkeskopAtgardsgrupper
} from "~app/common/models/constants";
import { CommandService, ICommand } from "~app/common/services/cqs/command.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { IKartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { CreateAtgardCommand } from "../commands/atgard/CreateAtgardCommand";
import { ResetSlutjusteraAtgardCommand } from "../commands/atgard/ResetSlutjusteraAtgardCommand";
import { SlutjusteraAtgardCommand } from "../commands/atgard/SlutjusteraAtgardCommand";
import { UpdateAtgardCommand } from "../commands/atgard/UpdateAtgardCommand";
import { Atgard, AtgardEntity, SkogsvardEntity, VKAtgardArbetsorderDTO } from "../models/atgard-models";
import { AtgardStatusExtraKartinfo } from "../models/atgardstatus/atgardstatuses-models";
import { Kontrakt } from "../models/kontrakt-models";
import { Planeringsstatus } from "../models/planeringsstatus";
import { Atgardstyper } from "../models/virkeskopAtgardstyper";
import { KontraktMediatorService } from "../services/kontrakt-mediator.service";
import { KartgeometriVirkeskopDrivningspunktRemapper } from "../utils/KartgeometriVirkeskopDrivningspunktRemapper";
import { VKAtgardHelper } from "../utils/vkatgard-helper";
import { AtgardKedjaMediatorService } from "./atgardkedja-mediator.service";
import { AvtalMediatorService } from "./avtal-mediator.service";
import { AvtalService } from "./avtal.service";
import { AvverkningskostnaderMediatorService } from "./avverkningskostnader-mediator.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { SkogsvardBetalningMediatorService } from "./skogsvard-betalning-mediator.service";
import { SkogsvardService } from "./skogsvard.service";
import { VkKartaMediatorService } from "./vk-karta-mediator.service";

@Injectable()
export class AtgardMediatorService implements OnDestroy {
    readonly calculatedMedelstam$ = new Subject<number>();
    readonly nettoareal$: Observable<number>;
    readonly onDeleteCallbacks: (() => boolean)[] = [];

    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly atgardSrc = new ReplaySubject<Atgard>(1);
    private readonly atgardUpdateSrc = new Subject<Atgard>();
    private readonly avtalService = inject(AvtalService);
    private readonly avverkningskostnaderMediatorService = inject(AvverkningskostnaderMediatorService);
    private readonly destroy$ = new Subject<void>();
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly loadingSrc = new Subject<boolean>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly removedSrc = new Subject<void>();
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);
    private readonly commandService = inject(CommandService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly skogsvardBetalningMediatorService = inject(SkogsvardBetalningMediatorService);

    private readonly skogsvardService = inject(SkogsvardService);

    private localAtgard: Atgard;

    constructor() {
        const kontraktMediatorService = this.kontraktMediatorService;
        this.atgardUpdateSrc
            .pipe(
                withLatestFrom(this.kontraktMediatorService.kontrakt$),
                takeUntil(this.destroy$)
            )
            .subscribe(([
                atgard,
                kontrakt
            ]) => this.handleAtgardUpdate(kontrakt, atgard));

        this.nettoareal$ = kontraktMediatorService
            .getKontraktNettoareal(x => x.atgardGuid === this.atgard?.guid);
    }
    get atgard(): Atgard {
        return this.localAtgard;
    }

    get atgard$() {
        return this.atgardSrc.asObservable();
    }

    get atgardUpdate$() {
        return this.atgardUpdateSrc.asObservable();
    }

    get loading$() {
        return this.loadingSrc.asObservable();
    }

    get removed$() {
        return this.removedSrc.asObservable();
    }

    initAtgard(entity: AtgardEntity) {
        if (entity?.id) {
            this.loadAtgardFromDB(entity.id);
        } else {
            const atgard = new Atgard();
            atgard.atgardstyp = entity.name;
            atgard.beteckning = entity.name;
            atgard.guid = entity.guid;
            atgard.avverkningsTyp = entity.avverkningsTyp;
            atgard.arbetsorder = new VKAtgardArbetsorderDTO();

            if (entity.avverkningsTyp?.kod === Atgardstyper.Leveransvirke) {
                atgard.atgardsnamn = this.kontraktMediatorService.kontrakt.benamning;
                atgard.atgardsgrupp = VirkeskopAtgardsgrupper.Leveransvirke;
                atgard.avverkningsTyp.grupp = Atgardstyper.Leveransvirke;
                atgard.status = Planeringsstatus.UnderPlanering;
            } else {
                atgard.atgardsnamn = entity.name;
                atgard.status = Planeringsstatus.UnderPlanering;
            }

            this.handleAtgardCreated(this.kontraktMediatorService.kontrakt, atgard);

            if (this.kontraktMediatorService.kontrakt.affarsform !== VirkeskopAffarsformer.Leveransvirke) {
                this.avverkningskostnaderMediatorService.create(atgard.guid);
            }

            // uppdatera kontraktmediatorn med den helt nya instansen
            this.kontraktMediatorService.updateAtgard(atgard);

            this.scheduleAtgard(atgard);
        }
    }

    async initSkogsvard(skogsvardEntity: SkogsvardEntity) {
        if (skogsvardEntity?.id) {
            this.loadAtgardFromDB(skogsvardEntity.id);
        } else {
            const kontrakt = this.kontraktMediatorService.kontrakt;
            // Skapa skogsvård utan att kopiera från en åtgärd
            if (!skogsvardEntity.createByCopy) {
                const skogsvardDestination = this.copyAtgardSkogsvard(skogsvardEntity, null);

                this.handleAtgardCreated(kontrakt, skogsvardDestination);
                this.kontraktMediatorService.updateAtgard(skogsvardDestination);
                this.scheduleAtgard(skogsvardDestination);

                // Skapa skogsvård från en åtgärd på samma kontrakt
            } else if (skogsvardEntity.createByCopy.copyFromKontraktGuid === this.kontraktMediatorService.kontrakt.guid) {
                const atgardSource = this.kontraktMediatorService.atgarder.find(x => x.guid === skogsvardEntity.createByCopy.copyFromAtgardGuid);
                const skogsvardDestination = this.copyAtgardSkogsvard(skogsvardEntity, atgardSource);

                this.handleAtgardCreated(kontrakt, skogsvardDestination);
                // Hämta Geometri
                if (this.shouldCopyGeometri(skogsvardEntity)) {
                    this.vkKartaMediatorService.copyAtgardToSkogsvardAtgardGeometri(kontrakt.guid, atgardSource.geometri, skogsvardDestination.guid);
                }
                this.kontraktMediatorService.updateAtgard(skogsvardDestination);
                this.scheduleAtgard(skogsvardDestination);
                if (atgardSource) {
                    this.atgardKedjaMediatorService.createAtgardKedjakoppling(atgardSource, skogsvardDestination);
                }
                // Skapa skogsvård från en åtgärd på ett externt kontrakt
            } else {
                await this.handleSkogsvardExternalContract(skogsvardEntity, kontrakt);
            }
        }
    }

    getAddKartgeometrier(copyFromKontraktGuid: string) {
        this.skogsvardService.getAllKartgeomtrierForKontrakt(copyFromKontraktGuid).subscribe(x => {
            const geometrier = x.map(element => ({
                anteckning: element.anteckning,
                geometri: element.geometri,
                subtyp1: element.subtyp1,
                subtyp2: element.subtyp2,
                subtyp3: element.subtyp3,
                guid: GuidUtils.newGuid(),
                etikett: element.etikett,
                karttext: element.karttext,
                tillganglighetsklass: element.tillganglighetsklass,
                hansynAtgardTyp: element.hansynAtgardTyp,
                externNyckel: element.externNyckel,
                isNew: true
            } as IKartgeometri));

            KartgeometriVirkeskopDrivningspunktRemapper.remapFromBackEndModel(geometrier);
            this.kontraktMediatorService.addContractGeometries(geometrier);
        });
    }

    isSkogsvard(): boolean {
        return VKAtgardHelper.isAtgardsGruppSkogsvard(this.atgard.atgardsgrupp);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    removeAtgard(): void {
        const result = this.onDeleteCallbacks.every(fn => fn());
        if (!result) {
            return;
        }
        const messages = [];
        if (this.atgard.hasForskottBetalning) {
            messages.push({
                severity: MessageSeverity.Warning,
                message: "Det förskott finns upplagt på kontraktet. Kontrollera att kvarstående åtgärders volym täcker utbetalningen."
            });
        }
        const settings = new ModalSettings("Är du säker på att du vill ta bort åtgärden?",
            "Observera att alla uppgifter på åtgärden kommer att tas bort från kontraktet. Välj Nej för att behålla åtgärden.");
        settings.messages = messages;
        this.raskModalService.confirm(settings)
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    this.setRemoved();
                    this.kontraktMediatorService.removeAtgard(this.localAtgard.guid);
                }
            });
    }

    setRemoved(): void {
        this.removedSrc.next();
        this.removedSrc.complete();
    }

    update(atgard: Atgard): void {
        this.atgardUpdateSrc.next(atgard);
        this.kontraktMediatorService.atgardUpdate$.next(atgard);
    }

    updateExtraKartInfo(extraKartInfo: AtgardStatusExtraKartinfo) {
        this.localAtgard.extraKartInfo = extraKartInfo;
        this.update(this.localAtgard);
    }

    slutjusteraAtgard(atgardGuid: string, kontraktId?: number, tidpunkt?: Date): Observable<any> {
        let slutjusteraAtgard$;

        if(tidpunkt) {
            slutjusteraAtgard$ = this.executeCommand(new SlutjusteraAtgardCommand(atgardGuid));
        } else {
            slutjusteraAtgard$ = this.executeCommand(new ResetSlutjusteraAtgardCommand(atgardGuid));
        }

        const loadBetalningar$ = this.skogsvardBetalningMediatorService.loadbetalningarForKontrakt(kontraktId);

        return concat(slutjusteraAtgard$, loadBetalningar$)
            .pipe(
                last()
            );
    }

    private executeCommand(cmd: ICommand): Observable<any> {
        this.commandService.addTransactionCommand(cmd);
        return this.commandService.pushCommandQueueTransaction().pipe(
            tap(() => {
                this.commandService.reset();
                this.avtalMediatorService.setAtgardSlutjusterad();
            })
        );
    }

    private async handleSkogsvardExternalContract(skogsvardEntity: SkogsvardEntity, kontrakt: Kontrakt) {
        const atgardSource = await firstValueFrom(this.avtalService.getAtgard(skogsvardEntity.createByCopy.copyFromAtgardId));
        const skogsvardDestination = this.copyAtgardSkogsvard(skogsvardEntity, atgardSource);

        this.handleAtgardCreated(kontrakt, skogsvardDestination);
        // Hämta Geometri
        if (this.shouldCopyGeometri(skogsvardEntity)) {
            this.vkKartaMediatorService.copyAtgardToSkogsvardAtgardGeometri(kontrakt.guid, atgardSource.geometri, skogsvardDestination.guid);
        }
        this.kontraktMediatorService.updateAtgard(skogsvardDestination);
        this.scheduleAtgard(skogsvardDestination);
        this.atgardKedjaMediatorService.enqueueExternalKedjaCheck(atgardSource, skogsvardDestination);
        if (skogsvardEntity.createByCopy.copyContractGeometries) {
            this.getAddKartgeometrier(skogsvardEntity.createByCopy.copyFromKontraktGuid);
        }
    }

    private copyAtgardSkogsvard(skogsvardEntity: SkogsvardEntity, atgardSource: Atgard) {
        const skogsvardDestination = new Atgard();

        skogsvardDestination.atgardstyp = skogsvardEntity.typ;
        skogsvardDestination.beteckning = skogsvardEntity.name;
        skogsvardDestination.guid = skogsvardEntity.guid;
        skogsvardDestination.atgardsnamn = skogsvardEntity.name;
        skogsvardDestination.atgardsgrupp = skogsvardEntity.grupp;
        skogsvardDestination.nettoareal = atgardSource ? atgardSource.nettoareal : 0;
        skogsvardDestination.status = Planeringsstatus.UnderPlanering;
        skogsvardDestination.malklass = atgardSource ? atgardSource.malklass : undefined;

        skogsvardDestination.foryngringssatt = skogsvardEntity.typ === SkogsvardAtgardsTyp.markberedningSadd ||
            skogsvardEntity.typ === SkogsvardAtgardsTyp.foryngringSadd
            ? SkogsvardForyngringssatt.sadd : null;

        if (skogsvardEntity.grupp !== SkogsvardAtgardsGrupp.rojning && atgardSource) {
            skogsvardDestination.barighet = atgardSource.barighet;
            skogsvardDestination.ytstruktur = atgardSource.ytstruktur;
            skogsvardDestination.lutning = atgardSource.lutning;
        }

        if (skogsvardDestination.atgardsgrupp === SkogsvardAtgardsGrupp.markberedning) {
            skogsvardDestination.skonsamMarkbehandling = "Nej";
        }

        if (skogsvardDestination.atgardsgrupp === SkogsvardAtgardsGrupp.foryngring && atgardSource?.atgardsgrupp === SkogsvardAtgardsGrupp.markberedning) {
            skogsvardDestination.markfuktighet = atgardSource.markfuktighet;
            skogsvardDestination.kornstorlek = atgardSource.kornstorlek;
            skogsvardDestination.slutrapportMeddelasMarkagare = atgardSource.slutrapportMeddelasMarkagare;
        }

        if (skogsvardDestination.atgardsgrupp === SkogsvardAtgardsGrupp.rojning) {
            skogsvardDestination.atgardsmetod = SkogsvardAtgardsMetod.motormanuell;
        }

        if (skogsvardDestination.atgardsgrupp === SkogsvardAtgardsGrupp.inventering) {
            skogsvardDestination.atgardsmetod = SkogsvardAtgardsMetod.objektiv;
        }

        return skogsvardDestination;
    }

    private handleAtgardCreated(kontrakt: Kontrakt, atgard: Atgard): void {
        const command = new CreateAtgardCommand(atgard, kontrakt.guid);
        this.kontraktCommandService.update(command);
    }

    private handleAtgardUpdate(kontrakt: Kontrakt, atgard: Atgard): void {
        const command = new UpdateAtgardCommand(atgard, kontrakt.guid);
        this.kontraktCommandService.update(command);
    }

    private loadAtgardFromDB(atgardId: number) {
        this.loadingSrc.next(true);
        this.kontraktMediatorService
            .getAtgard(atgardId)
            .subscribe(
                {
                    next: (atgard) => {
                        this.localAtgard = atgard;
                        this.atgardSrc.next(atgard);
                    },
                    error: (error) => {
                        console.error(error);
                    },
                    complete: () => {
                        this.loadingSrc.next(false);
                    }
                }
            );
    }

    private scheduleAtgard(atgard: Atgard) {
        asapScheduler.schedule(() => {
            this.localAtgard = atgard;
            this.atgardSrc.next(atgard);
            this.update(atgard);
        });
    }

    private shouldCopyGeometri(skogsvardEntity: SkogsvardEntity): boolean {
        return !Atgard.isPlantleverans(skogsvardEntity.grupp);
    }
}
